import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  FormControls,
  InfoTooltip,
  Switch,
  TextField,
  GenerateAIButton,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { TEST_TEMPLATE } from '@elementary/common-ui/constants/queryKeys';
import { Controller, UseFormReturn } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import * as api from 'testManagement/services/api';
import { fontWeight, space } from '@elementary/common-ui/style/utils';
import SeveritySelector from '../SeveritySelector';
import WhereClauseControl from '../WhereClauseControl';
import YamlEditor from '../YamlEditor';
import { getYamlFormResourceOptions } from '../YamlEditor/yamlFormResourceOptions';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';

const Page = styled(FlexColumn)`
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: ${({ theme }) => theme.spacing(3)};
  padding-left: ${({ theme }) => theme.spacing(3)};
`;

const RightPane = styled(FlexColumn)`
  width: 420px;
  flex-shrink: 0;
  min-height: 0;
  height: 100%;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(0, 3, 0, 4)};
`;

const LeftPane = styled(FlexColumn)`
  flex: 1;
  gap: ${({ theme }) => theme.spacing(3)};
  min-height: 0;
  width: 100%;
`;

const CenteredHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 960px;
  margin-inline: auto;
  padding-right: ${({ theme }) => theme.spacing(4)};
`;

const AiContainer = styled(Stack)`
  display: none; // remove to restore the ai feature
  flex-direction: row-reverse;
  gap: ${space('lg')};
  width: 100%;
`;

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  appliesTo: 'column' | 'table';
  editMode?: boolean;
  columnsResourcesData?: ColumnResource[];
  tableResourcesData?: Resource[];
};

const YamlTest = ({
  methods,
  appliesTo,
  editMode,
  columnsResourcesData = [],
  tableResourcesData = [],
}: Props) => {
  const { control, setValue, register, watch, getValues } = methods;

  const [isConfigExpanded, setIsConfigExpanded] = useState(false);
  const [showTextbox, setShowTextbox] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const yamlTemplateName = watch('yamlTemplateName');
  const yaml = watch('yaml');
  const resources = watch('resources');
  const selectedEnvId = useSelectedEnv();
  const { data: account } = useAccount();
  const appliedTemplateNameRef = useRef<string | undefined>();

  const { data: selectedTemplate } = useQuery(
    [TEST_TEMPLATE, yamlTemplateName, appliesTo],
    () => api.getTestTemplateData(yamlTemplateName!, selectedEnvId!),
    { enabled: !editMode && !!yamlTemplateName },
  );

  useEffect(() => {
    if (editMode) return;
    if (!yamlTemplateName) {
      appliedTemplateNameRef.current = undefined;
      return;
    }
    const templateYaml = selectedTemplate?.config_template_yaml;
    if (!templateYaml) return;
    if (appliedTemplateNameRef.current === yamlTemplateName) return;
    appliedTemplateNameRef.current = yamlTemplateName;
    setValue('yaml', templateYaml);
  }, [selectedTemplate, yamlTemplateName, editMode, setValue]);

  const allowConditionalSeverity =
    editMode || selectedTemplate?.test_template.supports_conditional_severity;

  useEffect(() => {
    if (editMode || !selectedTemplate) return;
    methods.setValue('severity', { type: 'error' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const {
    mutateAsync: generateTestConfiguration,
    isLoading: isGenerateTestConfigurationLoading,
  } = useMutation(async (inputText?: string) =>
    api.generateTestConfiguration(
      selectedEnvId!,
      getValues('resources')[0],
      selectedTemplate?.test_template.test_name!,
      yaml || '',
      appliesTo,
      inputText ? userInput : undefined,
    ),
  );

  const handleTextboxSubmit = async () => {
    setIsSubmitting(true);
    try {
      const aiTestConfig = await generateTestConfiguration(userInput);
      setValue('yaml', aiTestConfig.yaml_config);
      setValue('description', aiTestConfig.test_description);
      setShowTextbox(false);
      setUserInput('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateButtonClick = () => {
    if (!showTextbox) {
      setShowTextbox(true);
    }
  };

  const handleYamlChange = (nextYaml: string, isValid: boolean) => {
    if (nextYaml !== getValues('yaml')) {
      setValue('yaml', nextYaml, { shouldDirty: !editMode });
    }
    setValue('yamlValid', isValid);
  };

  const disabledAiTooltip = account?.ai_features_enabled
    ? undefined
    : 'Access to AI features and agents required';

  const editorYaml =
    yaml !== undefined
      ? yaml
      : (!editMode ? selectedTemplate?.config_template_yaml : undefined) ?? '';

  const isTemplatePending =
    !editMode && !!yamlTemplateName && !selectedTemplate;

  const resourceOptions = useMemo(
    () =>
      getYamlFormResourceOptions({
        resourceIds: resources ?? [],
        appliesTo,
        columnsResourcesData,
        tableResourcesData,
      }),
    [appliesTo, columnsResourcesData, resources, tableResourcesData],
  );

  const hasConfigSchema = !!selectedTemplate?.config_schema;

  const shouldShowFormDescription = hasConfigSchema || isTemplatePending;

  return (
    <Page>
      <Flex sx={{ flex: 1, minHeight: 0 }}>
        <FlexColumn sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
          <LeftPane>
            <CenteredHeader>
              <FlexColumn sx={{ gap: 0.5 }}>
                <Typography sx={{ fontWeight: fontWeight('medium') }}>
                  Test configuration
                </Typography>
                {editMode ? (
                  <Typography sx={{ color: 'content.secondary' }}>
                    Editing this section will be supported soon
                  </Typography>
                ) : (
                  <Typography sx={{ color: 'content.secondary' }}>
                    {shouldShowFormDescription
                      ? `Configure your test using the form below. Switch to YAML for advanced configuration. The test will be added to your dbt project's test configurations.`
                      : `Configure your test using the YAML configuration below. The test will be added to your dbt project's test configurations.`}
                  </Typography>
                )}
              </FlexColumn>
            </CenteredHeader>

            <YamlEditor
              value={editorYaml}
              onChange={handleYamlChange}
              configSchema={selectedTemplate?.config_schema}
              testName={selectedTemplate?.test_template.test_name}
              readOnly={editMode || isGenerateTestConfigurationLoading}
              grayedOut={editMode || isGenerateTestConfigurationLoading}
              isPending={isTemplatePending}
              resourceOptions={resourceOptions}
            />

            {account?.ai_features_enabled && (
              <AiContainer>
                <Tooltip title={disabledAiTooltip}>
                  <div>
                    <GenerateAIButton
                      sx={{ alignSelf: 'flex-end' }}
                      onClick={handleGenerateButtonClick}
                      loading={
                        isGenerateTestConfigurationLoading || isSubmitting
                      }
                      disabled={!!disabledAiTooltip || isSubmitting}
                    >
                      {isGenerateTestConfigurationLoading || isSubmitting
                        ? 'Processing request...'
                        : 'Configure test with AI'}
                    </GenerateAIButton>
                  </div>
                </Tooltip>
                {showTextbox && (
                  <TextField
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Describe your test or requirements..."
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleTextboxSubmit();
                      }
                    }}
                  />
                )}
              </AiContainer>
            )}
          </LeftPane>
        </FlexColumn>

        <Divider orientation="vertical" flexItem />

        <RightPane>
          <FormControls.FieldsWrapper sx={{ pb: 3 }}>
            <TableTestDescriptionField methods={methods} showCustomTestName />

            <TestMetadataForm methods={methods} />

            <FormControls.FieldsWrapper>
              <FormControls.TestAccordion
                variant="secondary"
                summary={
                  <Typography sx={{ fontWeight: fontWeight('medium') }}>
                    Advanced configuration
                  </Typography>
                }
                expanded={isConfigExpanded}
                onChange={(_, val) => setIsConfigExpanded(val)}
              >
                <FlexColumn sx={{ pt: '18px', gap: space('xxl') }}>
                  <Controller
                    name="severity"
                    control={control}
                    render={({ field }) => (
                      <SeveritySelector
                        {...field}
                        allowConditional={allowConditionalSeverity}
                      />
                    )}
                  />

                  {!editMode && (
                    <>
                      <FormControls.FormTitle>Others</FormControls.FormTitle>

                      <FormControls.Row>
                        <FormControls.FormText>Limit:</FormControls.FormText>
                        <TextField
                          sx={{ width: '134px' }}
                          placeholder="Set limit"
                          numeric
                          {...register('limit')}
                        />
                        <InfoTooltip title="Limit the number of failures that will be returned by the test query" />
                      </FormControls.Row>

                      <FormControls.Row>
                        <FormControls.FormText>
                          Store failure data:
                        </FormControls.FormText>
                        <Controller
                          name="shouldStoreFailures"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={!!field.value}
                              onChange={(_, checked) => field.onChange(checked)}
                              onBlur={field.onBlur}
                              name={field.name}
                              inputRef={field.ref}
                            />
                          )}
                        />
                        <InfoTooltip title="Whether or not the test failures are stored in the database" />
                      </FormControls.Row>

                      <Controller
                        name="whereClause"
                        control={control}
                        render={({ field }) => (
                          <WhereClauseControl {...field} />
                        )}
                      />
                    </>
                  )}
                </FlexColumn>
              </FormControls.TestAccordion>
            </FormControls.FieldsWrapper>
          </FormControls.FieldsWrapper>
        </RightPane>
      </Flex>
    </Page>
  );
};

export default YamlTest;
